import React, { useCallback, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useFlexLayout, useTable } from 'react-table';
import { FormattedMessage } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FlexColumn } from 'ui-components/components/flex';
import { messages as modalMessages } from 'ui-components/components/modal/messages';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';

import { messages } from './messages';
import HelpArticleLink from '../../../../components/help-article-link';
import { SimpleModal } from '../../../../components/simple-modal';
import { useOnComponentMount } from '../../../../hooks/use-on-component-mount';
import { authenticateDomain, createDomain, updateDomain } from '../api-client';
import { createCopyToClipboardColumnDefinition } from '../table-cells/copy-to-clipboard-cell';
import { DomainStatus } from '../domain-status';
import { AdvancedOptionsSection } from './advanced-options-section';
import { DomainRecordPropType } from '../prop-types';
import { linkIDs } from '../../../../services/link-service/links';

import style from './verify-domain-modal.module.less';

const columns = [
  createColumnDefinitionBuilder({
    accessor: 'type',
    Header: <FormattedMessage {...messages.typeColumnHeader} />,
    width: 1,
  })(),
  createColumnDefinitionBuilder({
    accessor: 'host',
    Header: <FormattedMessage {...messages.hostColumnHeader} />,
    width: 3,
  })(),
  createCopyToClipboardColumnDefinition({
    accessor: 'host',
    id: 'copyCnameHost',
  }),
  createColumnDefinitionBuilder({
    accessor: 'data',
    Header: <FormattedMessage {...messages.dataColumnHeader} />,
    width: 4,
  })(),
  createCopyToClipboardColumnDefinition({
    accessor: 'data',
    id: 'copyCnameData',
  }),
];

const createDmarcPolicyRecord = (domainName) => {
  return {
    type: <FormattedMessage {...messages.txtType} />,
    host: `_dmarc.${domainName}`,
    data: 'v=DMARC1; p=none',
  };
};

const createDnsRecordsWithDmarcPolicy = (dnsRecords, domainName) => {
  const realRecords = Object.values(dnsRecords).map((value) => {
    return {
      isValid: value.isValid,
      type: value.type?.toUpperCase(),
      host: value.host,
      data: value.data,
    };
  });

  return [...realRecords, createDmarcPolicyRecord(domainName)];
};

const getSubdomain = (dnsRecords) => {
  const chunks = dnsRecords?.mailCName.host.split('.') || [];
  return chunks.length > 2 ? chunks[0] : '';
};

const getDkimSelector = (dnsRecords) => {
  return dnsRecords?.dKim2.host.split('2.')[0] || '';
};

export const VerifyDomainModal = ({ onClose, domain, onDomainAuthenticated, onDomainCreated, onDomainUpdated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticatingDomain, setIsAuthenticatingDomain] = useState(false);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [hasAuthenticationError, setHasAuthenticationError] = useState(false);
  const [subdomain, setSubdomain] = useState(() => getSubdomain(domain?.dnsRecords));
  const [dkimSelector, setDkimSelector] = useState(() => getDkimSelector(domain?.dnsRecords));
  const [hasUpdateError, setHasUpdateError] = useState(false);

  useOnComponentMount(() => {
    if (!domain.dnsRecords) {
      setIsLoading(true);
      setHasLoadingError(false);

      createDomain({
        email: domain.email,
      })
        .then((response) => {
          onDomainCreated(response.data);
          setSubdomain(getSubdomain(response.data?.dnsRecords));
          setDkimSelector(getDkimSelector(response.data?.dnsRecords));
        })
        .catch(() => {
          setHasLoadingError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  });

  const onAuthenticate = useCallback(() => {
    setIsAuthenticatingDomain(true);
    setHasAuthenticationError(false);

    authenticateDomain({
      email: domain.email,
    })
      .then((response) => {
        const updatedDomain = response.data;
        onDomainAuthenticated(updatedDomain);

        if (updatedDomain.domainVerificationStatus !== DomainStatus.Authenticated) {
          setHasAuthenticationError(true);
        }
      })
      .catch(() => {
        setHasAuthenticationError(true);
      })
      .finally(() => {
        setIsAuthenticatingDomain(false);
      });
  }, [domain, onDomainAuthenticated]);

  const updateDnsRecords = useCallback(() => {
    setIsLoading(true);
    setHasUpdateError(false);

    return updateDomain({
      id: domain.domainId,
      subdomain,
      selector: dkimSelector,
    })
      .then((response) => {
        onDomainUpdated(response.data);
      })
      .catch(() => {
        setHasUpdateError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dkimSelector, domain.domainId, onDomainUpdated, subdomain]);

  const dnsRecordsWithDmarcPolicy = useMemo(() => {
    return domain.dnsRecords ? createDnsRecordsWithDmarcPolicy(domain.dnsRecords, domain.name) : [];
  }, [domain.dnsRecords, domain.name]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: dnsRecordsWithDmarcPolicy,
    },
    useFlexLayout,
  );

  return (
    <SimpleModal
      actionMessage={messages.checkStatusButtonLabel}
      cancelMessage={modalMessages.modalCancel}
      headerMessage={messages.verifyDomainModalHeader}
      isActionDisabled={isLoading || isAuthenticatingDomain}
      onAction={onAuthenticate}
      onCancel={onClose}
      onClose={onClose}
      size='large'
    >
      <FlexColumn className={style['verify-domain-modal-content']}>
        {hasAuthenticationError && (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={messages.domainAuthenticationError} />}
            layout={BannerLayout.Inline}
            status={BannerStatus.Error}
          />
        )}
        <div>
          <FlexColumn className={style['instructions-section']}>
            <Text textType={TextType.Paragraph} value={messages.verifyDomainInstructionsLine1} />
            <Text textType={TextType.Paragraph} value={messages.verifyDomainInstructionsLine2} />
          </FlexColumn>
          <HelpArticleLink articleId={linkIDs.SenderEmails} className={style['help-article-link']}>
            <FormattedMessage {...messages.learnMoreHere} />
          </HelpArticleLink>
        </div>
        {hasLoadingError ? (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={messages.dnsLoadingError} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Error}
          />
        ) : (
          <>
            <ReactTableWrapper
              getTableBodyProps={getTableBodyProps}
              getTableProps={getTableProps}
              headerGroups={headerGroups}
              isLoading={isLoading}
              prepareRow={prepareRow}
              rows={rows}
            />
            <AdvancedOptionsSection
              dkimSelector={dkimSelector}
              hasUpdateError={hasUpdateError}
              setDkimSelector={setDkimSelector}
              setSubdomain={setSubdomain}
              subdomain={subdomain}
              updateDnsRecords={updateDnsRecords}
            />
          </>
        )}
      </FlexColumn>
    </SimpleModal>
  );
};

VerifyDomainModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  domain: DomainRecordPropType.isRequired,
  onDomainAuthenticated: PropTypes.func.isRequired,
  onDomainCreated: PropTypes.func.isRequired,
  onDomainUpdated: PropTypes.func.isRequired,
};
